/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

import { Button, Divider, ShowMoreList, breakpoints } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { OccupationRolesContext } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { PortfolioBuilderContext } from 'bundles/account-profile/components/recruiting-profile-viewer/PortfolioBuilderContextProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileBanner from 'bundles/account-profile/components/recruiting-profile-viewer/components/ProfileBanner';
import BrowseProjectsBanner from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/BrowseProjectsBanner';
import BrowseProjectsDialogWithData from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/BrowseProjectsDialog';
import ProjectItem, {
  PortfolioProjectSkeleton,
} from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/ProjectItem';
import ViewProjectsDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/ViewProjectsDialog';
import { MAX_VISIBLE_PROJECTS } from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/constants';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import type { SharedLearnerProfile_PortfolioProjectsFragment as LearnerProfileProject } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/SharedLearnerProfile_portfolioProjects';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  withBottomMargin: () => css`
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  withTopMargin: () => css`
    margin-top: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-200);
    }
  `,
  ctaButton: () => css`
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
    }
  `,
};

type Props = {
  draftPortfolioProjects: LearnerProfileProject[];
  completedPortfolioProjects: LearnerProfileProject[];
};

const ProjectsBody = ({ completedPortfolioProjects, draftPortfolioProjects }: Props) => {
  const retrackedTrack = useRetracked();

  const { isProfileLoading, editable, retrackedEventingData, sharedEventingV3Data } = useContext(ProfileContext);
  const { openPortfolioBuilder } = useContext(PortfolioBuilderContext);
  const { activelyHiringOccupationOptions, nonActivelyHiringOccupationOptions } = useContext(OccupationRolesContext);
  const [selectedCaseStudyItem, setSelectedCaseStudyItem] = useState<LearnerProfileProject | undefined>(undefined);
  const [showBrowseProjectsDialog, setShowBrowseProjectsDialog] = useState(false);

  const track = useTracker();
  const showAllProjectsVisibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_button',
    {
      button: {
        name: EVENTS.profileShowAllProjects,
      },
      ...sharedEventingV3Data,
    }
  );

  const addProjectVisibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    {
      button: {
        name: EVENTS.profileAddProject,
      },
      ...sharedEventingV3Data,
    }
  );

  const numOfCompletedProjects = completedPortfolioProjects.length;
  const numOfDraftProjects = draftPortfolioProjects.length;

  const trackingData = {
    // eslint-disable-next-line camelcase
    candidate_profile: retrackedEventingData.candidate_profile,
    program: retrackedEventingData.program,
  };

  const userIdString = String(user.get().id);
  const occupations = [...activelyHiringOccupationOptions, ...nonActivelyHiringOccupationOptions].map((option) => ({
    id: option.value,
    label: option.label,
  }));

  const handleBrowseProjectsClick = () => {
    setShowBrowseProjectsDialog(true);
  };

  const handleBrowseProjectsClose = () => {
    setShowBrowseProjectsDialog(false);
  };

  const renderPortfolioProjects = () => {
    const renderedPortfolioProjects = completedPortfolioProjects.map((project, idx) => (
      <div key={project.title}>
        <ProjectItem
          project={project}
          onViewCaseStudy={() => setSelectedCaseStudyItem(project)}
          onEdit={() =>
            openPortfolioBuilder({
              project,
              isPublished: true,
            })
          }
        />
        {idx !== numOfCompletedProjects - 1 && (
          <Divider color="light" css={[styles.withBottomMargin, styles.withTopMargin]} />
        )}
      </div>
    ));

    return selectedCaseStudyItem !== undefined
      ? [
          <ViewProjectsDialog
            key={`viewProjects~${selectedCaseStudyItem.title}`}
            isOpen={selectedCaseStudyItem !== undefined}
            onClose={() => setSelectedCaseStudyItem(undefined)}
            initialCompletedProject={selectedCaseStudyItem}
            completedPortfolioProjects={completedPortfolioProjects}
          />,
          ...renderedPortfolioProjects,
        ]
      : renderedPortfolioProjects;
  };

  if (isProfileLoading) {
    return (
      <div>
        <PortfolioProjectSkeleton />
        <Divider color="light" css={[styles.withBottomMargin, styles.withTopMargin]} />
        <PortfolioProjectSkeleton />
        <Divider color="light" css={[styles.withBottomMargin, styles.withTopMargin]} />
        <PortfolioProjectSkeleton />
      </div>
    );
  }

  if (numOfCompletedProjects > MAX_VISIBLE_PROJECTS) {
    return (
      <ShowMoreList
        numItemsToShow={MAX_VISIBLE_PROJECTS}
        controllerProps={{
          itemTypePlural: _t('projects'),
          // We need to override the collapsed label because we are including the BrowseProjectsBanner inside the list.
          // This causes the ShowMoreList to think that there is one extra item in the total number of items to show, so we need to override the label
          collapsedLabel: _t('Show all #{numOfCompletedProjects} projects', { numOfCompletedProjects }),
          ariaLabel: _t('Show all #{numOfCompletedProjects} projects', { numOfCompletedProjects }),
        }}
        onChange={(isExpanded) => {
          // I need to use the tracking function from useRetracked because at the time of writing, there's no way to pass a TrackedButton
          // into the <ShowMoreList> component. This is a temporary workaround. See https://coursera.atlassian.net/browse/LPD-406
          // for more details.
          if (isExpanded) {
            retrackedTrack({
              trackingData,
              trackingName: EVENTS.profileShowAllProjects,
              action: 'click',
            });

            track('click_button', {
              button: {
                name: EVENTS.profileShowAllProjects,
              },
              ...sharedEventingV3Data,
            });
          }
        }}
        ref={showAllProjectsVisibilityTrackingRef}
      >
        {renderPortfolioProjects()}
        {editable && (
          <div css={styles.withTopMargin}>
            <BrowseProjectsBanner
              onBrowseProjectsClick={handleBrowseProjectsClick}
              hasAtLeastOneProject={numOfCompletedProjects > 0}
            />
            <BrowseProjectsDialogWithData
              userId={userIdString}
              occupations={occupations}
              onClose={handleBrowseProjectsClose}
              isOpen={showBrowseProjectsDialog}
            />
          </div>
        )}
      </ShowMoreList>
    );
  }

  return (
    <div>
      {renderPortfolioProjects()}
      {editable && numOfCompletedProjects <= MAX_VISIBLE_PROJECTS && (
        <>
          {numOfDraftProjects > 0 && (
            <ProfileBanner
              customCss={[styles.withBottomMargin, numOfCompletedProjects ? styles.withTopMargin : undefined]}
              body={
                <FormattedMessage
                  message={
                    // We need two separate translation strings because we cannot have multiple plural swaps (project/projects and hasn't/haven't)
                    // within one translation string since it makes it hard to accurately translate (according to eslint)
                    numOfDraftProjects === 1
                      ? _t("It looks like you have {numOfDraftProjects} project that hasn't been added yet.")
                      : _t("It looks like you have {numOfDraftProjects} projects that haven't been added yet.")
                  }
                  numOfDraftProjects={numOfDraftProjects}
                />
              }
              ctaContent={
                <TrackedDiv
                  trackClicks={false}
                  withVisibilityTracking
                  requireFullyVisible={false}
                  trackingName={EVENTS.profileAddProject}
                >
                  <Button
                    ref={addProjectVisibilityTrackingRef}
                    variant="secondary"
                    iconPosition="before"
                    icon={<AddIcon />}
                    size="small"
                    onClick={() => {
                      retrackedTrack({
                        trackingName: EVENTS.profileAddProject,
                        trackingData: {},
                        action: 'click',
                      });

                      track('click_button', {
                        button: {
                          name: EVENTS.profileAddProject,
                        },
                        ...sharedEventingV3Data,
                      });

                      openPortfolioBuilder({
                        projectsList: draftPortfolioProjects,
                        isPublished: false,
                      });
                    }}
                    css={styles.ctaButton}
                  >
                    {_t('Add project')}
                  </Button>
                </TrackedDiv>
              }
            />
          )}

          <div css={styles.withTopMargin}>
            <BrowseProjectsBanner
              hasAtLeastOneProject={numOfCompletedProjects > 0}
              onBrowseProjectsClick={handleBrowseProjectsClick}
            />
            <BrowseProjectsDialogWithData
              userId={userIdString}
              occupations={occupations}
              onClose={handleBrowseProjectsClose}
              isOpen={showBrowseProjectsDialog}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectsBody;
